import * as Sentry from "@sentry/react";

const isDev = process.env.REACT_APP_NODE_ENV === "development";
const isProd = !isDev;

// High-signal routes for upsampling in APM
const CRITICAL_ROUTE_PATTERNS = [
  // Auth & entry
  /^\/login$/i,
  /^\/$/i,
  /^\/news(?:\/|$)/i,

  // Main areas
  /^\/club(?:\/|$)/i,
  /^\/files(?:\/|$)/i,
  /^\/messages(?:\/|$)/i,
  /^\/shop(?:\/|$)/i,
  /^\/forms(?:\/|$)/i,

  // Super-admin
  /^\/super-admin\/clubs(?:\/|$)/i,
  /^\/super-admin\/official-leagues(?:\/|$)/i,
  /^\/super-admin\/impersonate(?:\/|$)/i,
  /^\/super-admin\/roles(?:\/|$)/i,
];

function isCriticalRoute(name) {
  return !!name && CRITICAL_ROUTE_PATTERNS.some((re) => re.test(name));
}

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  release: `managerapp@${process.env.REACT_APP_VERSION || "0.0.0"}`,
  environment: process.env.REACT_APP_NODE_ENV || "production",

  // RRv6-aware integration
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      // Optional: fine-tune
      startTransactionOnPageLoad: true,
      startTransactionOnLocationChange: true,
    }),
    Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
  ],

  // Errors: keep all
  sampleRate: 1.0,

  // Performance (APM): upsample critical routes, keep a light baseline elsewhere
  tracesSampler: (ctx) => {
    if (ctx?.parentSampled === true) return true;

    const txName =
      ctx?.name ?? ctx?.transactionContext?.name ?? ctx?.data?.name ?? "";

    const path =
      ctx?.location?.pathname ??
      (typeof window !== "undefined" ? window.location.pathname : "");

    const candidate = String(txName || path || "");

    if (isCriticalRoute(candidate)) return isProd ? 0.3 : 1.0;
    return isProd ? 0.07 : 0.5;
  },

  // CPU profiling
  profilesSampleRate: isProd ? 0.05 : 1.0,

  // Session Replay
  replaysOnErrorSampleRate: isProd ? 0.25 : 1.0,
  replaysSessionSampleRate: isProd ? 0.005 : 0.2,

  // Hygiene
  maxBreadcrumbs: 50,
  maxValueLength: 2000,
  normalizeDepth: 4,
  sendClientReports: true,

  ignoreErrors: [
    /ResizeObserver loop limit exceeded/i,
    /NetworkError/i,
    /NonCriticalError/i,
    /ChunkLoadError/i,
    /Request aborted/i,
  ],
  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /safari-web-extension/i,
    /moz-extension:\/\//i,
  ],

  beforeSend(event) {
    if (event.level === "info") return null;
    const url = event.request?.url;
    if (url) {
      try {
        const u = new URL(url);
        u.search = ""; // strip query params (PII)
        event.request.url = u.toString();
      } catch {
        /* noop */
      }
    }
    return event;
  },

  beforeSendTransaction(tx) {
    const n = tx?.name || "";
    if (/\/health(z)?$/i.test(n)) return null;
    return tx;
  },
});

// Optional: global tag to filter Academy vs Backoffice in Sentry
Sentry.setTags({ app: "playerdex-console" });

export default Sentry;
